import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

const SOURCE_BLOCKS_SELECTOR = '[data-cart-page-more-blocks]';
const MIRRORED_BLOCK_SCRIPT_MATCHERS = ['/assets/block-badge.js', '/assets/block-review-summary.js'];

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  #cartPageBlocksAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    window.addEventListener('resize', this.#handleResize);
    this.addEventListener('click', this.#handleUpsellNavigation);
    this.addEventListener('scroll', this.#handleUpsellScroll, true);
    this.#syncCartPageBlocks();
    this.#queueUpsellNavigationUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('click', this.#handleUpsellNavigation);
    this.removeEventListener('scroll', this.#handleUpsellScroll, true);
    this.#cartPageBlocksAbortController?.abort();
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#syncCartPageBlocks();
    this.#queueUpsellNavigationUpdate();
  };

  #handleResize = () => {
    this.#queueUpsellNavigationUpdate();
  };

  #handleUpsellNavigation = (event) => {
    const control = event.target.closest('[data-cart-drawer-upsell-prev], [data-cart-drawer-upsell-next]');

    if (!control || !this.contains(control)) return;

    const upsell = control.closest('[data-cart-drawer-upsell]');
    const list = upsell?.querySelector('[data-cart-drawer-upsell-list]');

    if (!upsell || !list) return;

    const direction = control.hasAttribute('data-cart-drawer-upsell-prev') ? -1 : 1;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    list.scrollBy({
      left: list.clientWidth * direction,
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });

    this.#queueUpsellNavigationUpdate(upsell);
  };

  #handleUpsellScroll = (event) => {
    if (!(event.target instanceof Element) || !event.target.matches('[data-cart-drawer-upsell-list]')) return;

    const upsell = event.target.closest('[data-cart-drawer-upsell]');

    if (upsell) this.#queueUpsellNavigationUpdate(upsell);
  };

  open() {
    this.showDialog();
    this.#syncCartPageBlocks();
    this.#queueUpsellNavigationUpdate();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #queueUpsellNavigationUpdate(root = this) {
    requestAnimationFrame(() => this.#updateUpsellNavigation(root));
  }

  #updateUpsellNavigation(root = this) {
    const upsells = root.matches?.('[data-cart-drawer-upsell]')
      ? [root]
      : [...root.querySelectorAll('[data-cart-drawer-upsell]')];

    for (const upsell of upsells) {
      const list = upsell.querySelector('[data-cart-drawer-upsell-list]');
      const previous = upsell.querySelector('[data-cart-drawer-upsell-prev]');
      const next = upsell.querySelector('[data-cart-drawer-upsell-next]');

      if (!list || !previous || !next) continue;

      const maxScroll = Math.max(0, list.scrollWidth - list.clientWidth);
      const isOverflowing = maxScroll > 1;

      previous.disabled = !isOverflowing || list.scrollLeft <= 1;
      next.disabled = !isOverflowing || list.scrollLeft >= maxScroll - 1;
      upsell.toggleAttribute('data-cart-drawer-upsell-overflowing', isOverflowing);
    }
  }

  async #syncCartPageBlocks() {
    const slots = [...this.querySelectorAll('[data-cart-drawer-mirror-block]')];

    if (!slots.length) return;

    this.#cartPageBlocksAbortController?.abort();
    this.#cartPageBlocksAbortController = new AbortController();

    const { signal } = this.#cartPageBlocksAbortController;

    try {
      const response = await fetch(this.#cartPageBlocksUrl(), {
        credentials: 'same-origin',
        signal,
      });

      if (!response.ok) throw new Error(`Cart page mirror request failed with status ${response.status}`);

      const html = await response.text();
      const sourceDocument = new DOMParser().parseFromString(html, 'text/html');
      const sourceBlocks = sourceDocument.querySelector(SOURCE_BLOCKS_SELECTOR);

      await this.#loadMirroredBlockScripts(sourceDocument);

      for (const slot of slots) {
        const blockId = slot.dataset.cartDrawerMirrorBlock;
        const sourceBlock = blockId ? sourceBlocks?.querySelector(`[id*="${blockId}"]`) : null;

        this.#replaceSlotContent(slot, sourceBlock);
      }
    } catch (error) {
      if (error.name === 'AbortError') return;

      console.error(error);
      slots.forEach((slot) => this.#replaceSlotContent(slot, null));
    }
  }

  #cartPageBlocksUrl() {
    const source = this.dataset.cartDrawerMirrorSource || '/cart';
    const url = new URL(source, window.location.origin);
    const currentUrl = new URL(window.location.href);

    currentUrl.searchParams.forEach((value, key) => {
      if (!url.searchParams.has(key)) url.searchParams.set(key, value);
    });

    return url;
  }

  async #loadMirroredBlockScripts(sourceDocument) {
    const scripts = [...sourceDocument.querySelectorAll('script[src]')].filter((script) => {
      const src = script.getAttribute('src') || '';
      return MIRRORED_BLOCK_SCRIPT_MATCHERS.some((matcher) => src.includes(matcher));
    });

    await Promise.all(scripts.map((script) => this.#loadScript(script)));
  }

  #loadScript(sourceScript) {
    const src = sourceScript.src;

    if (!src || [...document.scripts].some((script) => script.src === src)) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const script = document.createElement('script');

      for (const { name, value } of sourceScript.attributes) {
        script.setAttribute(name, value);
      }

      script.onload = resolve;
      script.onerror = resolve;
      document.head.appendChild(script);
    });
  }

  #replaceSlotContent(slot, sourceBlock) {
    if (!sourceBlock) {
      slot.replaceChildren();
      slot.hidden = true;
      return;
    }

    const clonedBlock = sourceBlock.cloneNode(true);

    slot.replaceChildren(clonedBlock);
    slot.hidden = false;
    this.#reactivateScripts(slot);
  }

  #reactivateScripts(container) {
    container.querySelectorAll('script').forEach((script) => {
      const replacement = document.createElement('script');

      for (const { name, value } of script.attributes) {
        replacement.setAttribute(name, value);
      }

      replacement.textContent = script.textContent;
      script.replaceWith(replacement);
    });
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
